<template>
  <div
    class="comment-block"
    :data-comment="comment.id"
    :class="{
      'first-level': !parent,
      'editing': editing,
    }"
  >
    <template v-if="visible">
      <div class="content">
        <template v-if="editing">
          <comment-form :active="true" :comment="comment" :pending="updating" :parent="parent" @submitted="update" />
        </template>

        <comment-content
          class="comment-content"
          :comment="comment"
          :can-restore="canRestore"
          :can-reply="canEdit"
          :can-edit="canEdit"
          @remove="remove"
          @restore="restore"
          @edit="edit"
          @reply="reply"
        />
      </div>

      <div v-if="comment.answers && comment.answers.length" class="answers">
        <div v-for="answer in comment.answers" :key="answer.id">
          <conversation-comment
            :entity="entity"
            :entity-id="entityId"
            :comment="answer"
            :can-edit="canEdit"
            :parent="comment"
            :active-form="activeForm"
            @formOpened="$emit('formOpened', $event)"
            @commentAdded="$emit('commentAdded', $event)"
            @commentUpdated="$emit('commentUpdated', $event)"
            @commentRemoved="$emit('commentRemoved', $event)"
            @commentRestored="$emit('commentRestored', $event)"
          />
        </div>
      </div>

      <template v-if="replying">
        <div class="reply-form">
          <comment-form :active="true" :parent="parent" :pending="updating" @submitted="addAnswer" />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import CommentContent from "./CommentContent";
import CommentForm from "./AdaptiveCommentForm";

import { ADD_MESSAGE_ACTION, EDIT_MESSAGE_ACTION, REMOVE_MESSAGE_ACTION, RESTORE_MESSAGE_ACTION } from "./api";
import { CommentModel, updateComment } from "./comment-model";
import { COMMENT_ADDED, COMMENT_REMOVED, COMMENT_RESTORED, COMMENT_UPDATED } from "./events";

const EDIT_ACTION = "comment/actions/editing";
const REPLY_ACTION = "comment/actions/answering";

export default {
  name: "conversation-comment",

  components: {
    CommentContent,
    CommentForm,
  },
  props: ["comment", "entity", "entityId", "canEdit", "parent", "activeForm"],

  data() {
    return {
      canRestore: false,

      editing: false,
      replying: false,

      updating: false,
    };
  },

  computed: {
    visible() {
      if (!this.comment.deleted) return true;
      if (this.comment.answers.length) return true;
      if (this.canRestore) return true;
      return false;
    },
  },

  watch: {
    activeForm(v) {
      if (!v) return;

      const { id, type } = v;

      if (id !== this.comment.id) {
        this.editing = false;
        this.replying = false;
        return;
      }

      if (type === EDIT_ACTION) this.replying = false;
      if (type === REPLY_ACTION) this.editing = false;
    },
  },

  methods: {
    remove() {
      this.$store.dispatch(REMOVE_MESSAGE_ACTION, this.comment.id).then((res) => {
        if (res) {
          this.comment.deleted = true;
          this.canRestore = true;
          this.$emit(COMMENT_REMOVED, this.comment.id);
        }
      });
    },
    restore() {
      this.$store.dispatch(RESTORE_MESSAGE_ACTION, this.comment.id).then((res) => {
        if (res) {
          this.comment.deleted = false;
          this.canRestore = false;
          this.$emit(COMMENT_RESTORED, this.comment.id);
        }
      });
    },
    edit() {
      this.$emit("formOpened", {
        id: this.comment.id,
        type: EDIT_ACTION,
      });
      this.editing = true;
    },
    reply() {
      this.$emit("formOpened", {
        id: this.comment.id,
        type: REPLY_ACTION,
      });
      this.replying = true;
    },

    update(params) {
      this.updating = true;
      this.$store
        .dispatch(EDIT_MESSAGE_ACTION, {
          id: this.comment.id,
          ...params,
        })
        .then((res) => {
          if (res) {
            updateComment(this.comment, res.model);

            this.updating = false;
            this.editing = false;
            this.$emit(COMMENT_UPDATED, this.comment.id);
          } else {
            this.onError();
          }
        })
        .catch(() => {
          this.onError();
        });
    },

    addAnswer(params) {
      this.updating = true;
      this.$store
        .dispatch(ADD_MESSAGE_ACTION, {
          entity: this.entity,
          entityId: this.entityId,
          parentId: this.comment.id,
          ...params,
        })
        .then((res) => {
          if (res) {
            this.comment.answers = [...this.comment.answers, CommentModel(res.model)];
            this.updating = false;
            this.replying = false;
            this.$emit(COMMENT_ADDED, res.model.id);
          } else {
            this.onError();
          }
        })
        .catch(() => {
          this.onError();
        });
    },

    onError() {
      this.$notify({
        group: "center-error-message",
        data: {
          textMessage: "Произошла ошибка при отправке комментария. Попробуйте ещё раз.",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.comment-block {
  border-top: 1px solid #dfe4ef;
  padding-top: 12px;

  .content {
    padding-bottom: 15px;
  }

  &.first-level {
    & > .answers,
    & > .reply-form {
      padding-left: 44px;
    }

    &:last-child {
      border-bottom: 1px solid #dfe4ef;
    }
  }

  .reply-form {
    padding-top: 12px;
    border-top: 1px solid #dfe4ef;
    padding-bottom: 12px;
  }

  &.editing {
    & > .content {
      .comment-content {
        display: none;
      }
    }
  }

  @media screen and (max-width: 700px) {
    .reply-form {
      padding: 0;
      border: 0;
    }
    &.editing {
      .comment-content {
        display: block;
      }
    }

    &.first-level {
      & > .answers,
      & > .reply-form {
        padding-left: 20px;
      }
    }
  }
}
</style>
